import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IdCard } from 'lucide-react';
import { Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { linhasExtras, useCamposPersonalizados } from '../../componentes/formulario';
import { Sensivel } from '../../componentes/sensivel';
import { ErroRequisicao, get, mensagemDeErro } from '../../nucleo/api';
import { data, dinheiro, email, nomePessoa } from '../../nucleo/formato';
import { DocumentosCard, HabilidadesCard, HistoricoCard, Linha, OnboardingCard } from './FichaPartes';
import { COR_CONTRATO, COR_ESTADO, cpfFormatado, telefoneFormatado, type Ficha } from './tipos';

/**
 * "Meus dados" (21/10, Minha conta > Meus dados): a própria ficha de funcionário, só leitura, com os dados pessoais e o
 * salário (`GET /pessoas/meus-dados`, só sessão). Os mesmos cartões da ficha do Pessoas, sem ações; mudança se pede ao RH.
 * Sem ficha ligada ao usuário, o aviso de como ligar.
 */
export function MeusDados() {
  const { t } = useTranslation('pessoas');
  const defsExtras = useCamposPersonalizados('pessoas.funcionario');
  const q = useQuery({ queryKey: ['pessoas', 'meus-dados'], queryFn: () => get<Ficha>('/pessoas/meus-dados'), retry: false });
  if (q.isPending) return <Carregando />;
  if (q.isError) {
    if (q.error instanceof ErroRequisicao && q.error.corpo.code === 'funcionario.nao_vinculado') {
      return <Vazio titulo={t('meusDados.semFicha')} texto={mensagemDeErro(q.error)} icone={<IdCard className="size-5" aria-hidden />} />;
    }
    return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  }
  const f = q.data;
  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-3" data-meus-dados>
      <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
        <p className="text-sm text-tinta-3">{t('meusDados.texto')}</p>
        <Cartao titulo={t('ficha.dados')}>
          <dl className="grid gap-x-8 sm:grid-cols-2">
            <Linha rotulo={t('campos.nome')}>{nomePessoa(f.nome)}</Linha>
            <Linha rotulo={t('campos.cpf')}>
              <span className="numero">{cpfFormatado(f.cpf)}</span>
            </Linha>
            <Linha rotulo={t('campos.nascimento')}>{f.nascimento ? <Sensivel>{data(f.nascimento)}</Sensivel> : '-'}</Linha>
            <Linha rotulo={t('campos.email')}>{f.email ? email(f.email) : '-'}</Linha>
            <Linha rotulo={t('campos.telefone')}>
              <span className="numero">{f.telefone ? telefoneFormatado(f.telefone) : '-'}</span>
            </Linha>
            {linhasExtras(defsExtras, f.extras, f.nome).map((l) => (
              <Linha key={l.chave} rotulo={l.rotulo}>
                {l.valor}
              </Linha>
            ))}
          </dl>
        </Cartao>
        <Cartao titulo={t('ficha.contrato')}>
          <dl className="grid gap-x-8 sm:grid-cols-2">
            <Linha rotulo={t('campos.situacao')}>
              <Selo cor={COR_ESTADO[f.estado]}>{t(`estado.${f.estado}`)}</Selo>
            </Linha>
            <Linha rotulo={t('campos.contrato')}>
              <Selo cor={COR_CONTRATO[f.contrato_tipo]}>{t(`contrato.${f.contrato_tipo}`)}</Selo>
            </Linha>
            <Linha rotulo={t('campos.funcao')}>{f.funcao_nome ?? '-'}</Linha>
            <Linha rotulo={t('campos.departamento')}>{f.departamento_nome ?? '-'}</Linha>
            <Linha rotulo={t('campos.gestor')}>{f.gestor_nome ? nomePessoa(f.gestor_nome) : '-'}</Linha>
            <Linha rotulo={t('campos.calendario')}>{f.calendario_nome ?? '-'}</Linha>
            <Linha rotulo={t('campos.admissao')}>{f.admissao ? data(f.admissao) : '-'}</Linha>
            <Linha rotulo={t('campos.fimContrato')}>{f.fim_contrato ? data(f.fim_contrato) : '-'}</Linha>
            <Linha rotulo={t('campos.salario')}>
              <span className="numero">{f.salario_centavos === null ? '-' : dinheiro(f.salario_centavos, f.moeda)}</span>
            </Linha>
          </dl>
        </Cartao>
        <DocumentosCard ficha={f} podeEditar={false} />
        <HabilidadesCard ficha={f} podeEditar={false} />
        <HistoricoCard ficha={f} />
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        <OnboardingCard ficha={f} podeEditar={false} />
      </div>
    </div>
  );
}
